import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppSelector } from '../../../../../store';
import Modal from '../../../../../components/Modal';
import Icon from '../../../../../components/Icon';
import Button from '../../../../../components/Button';
import TextField from '../../../../../components/TextField';
import SearchableSelect from '../../../../../components/SearchableSelect';
import { FiMapPin, FiCalendar, FiUser, FiFileText, FiEdit2, FiSave, FiCheckCircle, FiXCircle, FiCheck, FiDownload } from 'react-icons/fi';
import { type NewLocationSurvey, updateNewLocationSurvey } from '../../service/newLocationSurveyService';
import { db } from '../../../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { sendEmailsWithErrorHandling } from '../../../../../api/emailService';
import { buildLocationSurveyEmail } from '../../email/locationSurveyEmailTemplates';
import { designationIsIncharge } from '../../../../../utils/designationRole';

type NewLocationSurveyDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    survey: NewLocationSurvey | null;
    onSuccess?: (message?: string, submittedSurvey?: NewLocationSurvey) => void;
    autoEdit?: boolean;
    onDownloadPDF?: (survey: NewLocationSurvey) => void;
    onToggleReadStatus?: (survey: NewLocationSurvey, newReadStatus: boolean) => Promise<void>;
};

type RecipientDetail = {
    id: string;
    name?: string;
    email?: string;
    role: 'assignedTo' | 'createdBy' | 'assignedBy';
};

export default function NewLocationSurveyDetailsModal({ isOpen, onClose, survey, onSuccess, autoEdit = false, onDownloadPDF, onToggleReadStatus }: NewLocationSurveyDetailsModalProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editedSurvey, setEditedSurvey] = useState<NewLocationSurvey | null>(null);
    const [recipientDetails, setRecipientDetails] = useState<RecipientDetail[]>([]);
    const [statusAction, setStatusAction] = useState<'approve' | 'reject' | 'cancel' | 'complete' | 'forward' | null>(null);
    const [showApproveEmailModal, setShowApproveEmailModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
    const [emailInputValue, setEmailInputValue] = useState('');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailPreviewHtml, setEmailPreviewHtml] = useState<string>('');
    const [emailPreviewSubject, setEmailPreviewSubject] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showForwardModal, setShowForwardModal] = useState(false);

    const [regionalRemarksInput, setRegionalRemarksInput] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReasonInput, setRejectionReasonInput] = useState('');
    const [rejectError, setRejectError] = useState<string | null>(null);

    const currentUser = useAppSelector((s) => s.user.currentUser);
    const psoProfile = useAppSelector((s) => s.user.pso);
    const uid = currentUser?.uid;
    const designation = psoProfile?.designation || '';
    const normalizedDesignation = useMemo(() => designation.replace(/_/g, ' ').toLowerCase(), [designation]);
    const userRole = typeof window !== 'undefined' ? sessionStorage.getItem('role') : null;
    const isBankUser = userRole === 'bank';
    const isIncharge = useMemo(() => isBankUser || designationIsIncharge(designation), [isBankUser, designation]);
    const isRegional = useMemo(() => normalizedDesignation.includes('regional'), [normalizedDesignation]);
    const isMIS = useMemo(() => normalizedDesignation.includes('mis'), [normalizedDesignation]);
    const isPsoRole = useMemo(() => !isBankUser && !isMIS && !isRegional, [isBankUser, isMIS, isRegional]);

    const transformSurvey = useCallback((source: NewLocationSurvey): NewLocationSurvey => {
        const sourceDirections = source.directions || {};
        const normalizedDirections = {
            front: sourceDirections.front ?? sourceDirections.north ?? '',
            back: sourceDirections.back ?? sourceDirections.south ?? '',
            left: sourceDirections.left ?? sourceDirections.west ?? '',
            right: sourceDirections.right ?? sourceDirections.east ?? ''
        };

        const sourceQuestions = source.questions || {};
        const normalizedQuestions = {
            fuelStationAvailable: sourceQuestions.fuelStationAvailable || '',
            spaceForParking: sourceQuestions.spaceForParking || '',
            spaceForVantagePoint: sourceQuestions.spaceForVantagePoint || '',
            spaceForEmergencyExit: sourceQuestions.spaceForEmergencyExit || '',
            electricityIssue: sourceQuestions.electricityIssue || '',
            ptclLineAvailable: sourceQuestions.ptclLineAvailable || '',
            otherAssessment: sourceQuestions.otherAssessment || 'No',
            otherAssessmentHazardous: sourceQuestions.otherAssessmentHazardous || 'No'
        };

        const hazardFlag = normalizedQuestions.fuelStationAvailable === 'Yes' || normalizedQuestions.otherAssessmentHazardous === 'Yes' || source.isHazardous === true;
        const hazardousDetails = normalizedQuestions.otherAssessment === 'Yes' ? (source.hazardousArea || '') : '';

        return {
            ...source,
            directions: normalizedDirections,
            questions: normalizedQuestions,
            hazardousArea: hazardousDetails,
            isHazardous: hazardFlag
        };
    }, []);

    useEffect(() => {
        if (survey) {
            setEditedSurvey(transformSurvey(survey));
            setRegionalRemarksInput(survey.regionalRemarks || '');
            setRejectionReasonInput(survey.rejectionReason || '');
        } else {
            setEditedSurvey(null);
            setRegionalRemarksInput('');
            setRejectionReasonInput('');
        }
        setIsEditing(false);
    }, [survey, transformSurvey]);

    const creatorDisplayName = useMemo(() => {
        const creator = recipientDetails.find((recipient) => recipient.role === 'createdBy');
        return creator?.name || creator?.email || editedSurvey?.createdByName || editedSurvey?.createdBy || 'N/A';
    }, [recipientDetails, editedSurvey?.createdBy]);

    useEffect(() => {
        if (showApproveEmailModal && editedSurvey) {
            const surveyWithUpdates = {
                ...editedSurvey,
                status: isRegional ? 'Forwarded' : 'Approved',
                regionalRemarks: regionalRemarksInput.trim()
            } as NewLocationSurvey;
            const { html, subject } = buildLocationSurveyEmail({ survey: surveyWithUpdates, createdByName: creatorDisplayName });
            setEmailPreviewHtml(html);
            setEmailPreviewSubject(subject);
        }
    }, [regionalRemarksInput, showApproveEmailModal, editedSurvey, isRegional, creatorDisplayName]);

    useEffect(() => {
        const fetchRecipients = async () => {
            if (!survey || !survey.bankId) {
                setRecipientDetails([]);
                return;
            }

            const idsToFetch: RecipientDetail[] = [];

            if (survey.assignedTo) {
                idsToFetch.push({ id: survey.assignedTo, role: 'assignedTo' });
            }
            if (survey.createdBy) {
                idsToFetch.push({ id: survey.createdBy, role: 'createdBy' });
            }
            if (survey.assignedBy) {
                idsToFetch.push({ id: survey.assignedBy, role: 'assignedBy' });
            }

            if (idsToFetch.length === 0) {
                setRecipientDetails([]);
                return;
            }

            try {
                const uniqueMap = new Map<string, RecipientDetail>();

                await Promise.all(idsToFetch.map(async (item) => {
                    if (uniqueMap.has(item.id)) return;
                    try {
                        const userDoc = await getDoc(doc(db, 'banks', survey.bankId as string, 'pos', item.id));
                        if (userDoc.exists()) {
                            const data = userDoc.data();
                            uniqueMap.set(item.id, {
                                ...item,
                                name: data?.name || undefined,
                                email: data?.email || undefined,
                            });
                        }
                    } catch (error) {
                        console.error('Error fetching recipient details:', error);
                    }
                }));

                setRecipientDetails(Array.from(uniqueMap.values()));
            } catch (error) {
                console.error('Error building recipient list:', error);
            }
        };

        fetchRecipients();
    }, [survey]);

    const status = useMemo(() => {
        const currentStatus = (editedSurvey?.status || '').toLowerCase();
        if (currentStatus) {
            return currentStatus;
        }
        if (editedSurvey?.assignedTo && editedSurvey.assignedTo !== editedSurvey.createdBy) {
            return 'assigned';
        }
        return 'completed';
    }, [editedSurvey?.status, editedSurvey?.assignedTo, editedSurvey?.createdBy]);

    const statusLabel = useMemo(() => {
        if (!editedSurvey) return 'Completed';
        return editedSurvey.status || (editedSurvey.assignedTo && editedSurvey.assignedTo !== editedSurvey.createdBy ? 'Assigned' : 'Completed');
    }, [editedSurvey]);

    const isAssignedStatus = status === 'assigned';
    const isSubmittedStatus = status === 'submitted' || status === 'submitted to regional';
    const isApprovedStatus = status === 'approved';

    const otherAssessment = editedSurvey?.questions?.otherAssessment || 'No';
    const otherAssessmentHazardous = editedSurvey?.questions?.otherAssessmentHazardous || 'No';
    const fuelHazard = editedSurvey?.questions?.fuelStationAvailable === 'Yes';
    const baseHazard = editedSurvey?.isHazardous ?? false;
    const hazardDetailsPresent = (editedSurvey?.hazardousArea?.trim() || '') !== '';
    const hazardFlag = Boolean(baseHazard || fuelHazard || otherAssessmentHazardous === 'Yes' || hazardDetailsPresent);


    const canEdit = useMemo(() => {
        if (!editedSurvey || !uid) return false;
        if (isBankUser) return true;
        if (status === 'assigned' || status === 'submitted' || status === 'submitted to regional' || status === 'rejected') {
            return isPsoRole && (editedSurvey.createdBy === uid || editedSurvey.assignedTo === uid);
        }
        return false;
    }, [editedSurvey, uid, status, isPsoRole, isBankUser]);

    useEffect(() => {
        if (autoEdit && canEdit && !isEditing) {
            setIsEditing(true);
        }
    }, [autoEdit, canEdit, isEditing]);

    if (!survey || !editedSurvey) return null;

    const handleInputChange = (field: string, value: string) => {
        setEditedSurvey({
            ...editedSurvey,
            [field]: value
        });
    };

    const handleDirectionChange = (direction: string, value: string) => {
        setEditedSurvey({
            ...editedSurvey,
            directions: {
                ...editedSurvey.directions,
                [direction]: value
            }
        });
    };

    const handleQuestionChange = (question: string, value: string) => {
        if (!editedSurvey) return;

        const currentQuestions = (editedSurvey.questions || {}) as Record<string, string>;
        const updatedQuestions = { ...currentQuestions, [question]: value };
        let updatedHazardousArea = editedSurvey.hazardousArea || '';

        if (question === 'otherAssessment') {
            if (value === 'Yes') {
                if (!updatedQuestions.otherAssessmentHazardous) {
                    updatedQuestions.otherAssessmentHazardous = 'No';
                }
            } else {
                updatedQuestions.otherAssessmentHazardous = 'No';
                updatedHazardousArea = '';
            }
        }

        if (question === 'otherAssessmentHazardous' && value !== 'Yes') {
            updatedQuestions.otherAssessmentHazardous = 'No';
        }

        const updatedHazardFlag = (updatedQuestions.fuelStationAvailable === 'Yes') || (updatedQuestions.otherAssessmentHazardous === 'Yes');

        setEditedSurvey({
            ...editedSurvey,
            questions: updatedQuestions,
            hazardousArea: updatedHazardousArea,
            isHazardous: updatedHazardFlag
        });
    };

    const handleOtherAssessmentDetailsChange = (value: string) => {
        if (!editedSurvey) return;
        const currentQuestions = (editedSurvey.questions || {}) as Record<string, string>;
        const updatedHazardFlag = (currentQuestions.fuelStationAvailable === 'Yes') || (currentQuestions.otherAssessmentHazardous === 'Yes');
        setEditedSurvey({
            ...editedSurvey,
            hazardousArea: value,
            isHazardous: updatedHazardFlag
        });
    };

    const normalizeEmail = (email: string) => email.trim().toLowerCase();

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleSave = async () => {
        if (!editedSurvey) return;

        setLoading(true);
        try {
            const sanitizedHazardousArea = otherAssessment === 'Yes' ? (editedSurvey.hazardousArea || '') : '';
            const savePayload = {
                ...editedSurvey,
                hazardousArea: sanitizedHazardousArea,
                isHazardous: (editedSurvey.questions?.fuelStationAvailable === 'Yes') || (editedSurvey.questions?.otherAssessmentHazardous === 'Yes'),
                status: isBankUser ? (editedSurvey.status || 'Submitted to Regional') : ((isAssignedStatus || status === 'rejected') ? 'Submitted to Regional' : (editedSurvey.status || 'Submitted to Regional'))
            };
            await updateNewLocationSurvey(editedSurvey.id, editedSurvey.bankId ?? null, savePayload);
            setIsEditing(false);
            onClose();
            if (onSuccess) {
                onSuccess('Survey submitted successfully', savePayload);
            }
        } catch (error) {
            console.error('Error saving survey:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        if (survey) {
            setEditedSurvey(transformSurvey(survey));
        }
        setIsEditing(false);
    };

    const handleStatusUpdate = async (
        nextStatus: 'Approved' | 'Rejected' | 'Cancelled' | 'Completed' | 'Forwarded',
        options?: {
            recipients?: string[];
            extraUpdates?: Partial<NewLocationSurvey>;
        }
    ) => {
        if (!editedSurvey) return;
        const actionKind: 'approve' | 'reject' | 'cancel' | 'complete' | 'forward' =
            nextStatus === 'Approved'
                ? 'approve'
                : nextStatus === 'Rejected'
                    ? 'reject'
                    : nextStatus === 'Cancelled'
                        ? 'cancel'
                        : nextStatus === 'Completed'
                            ? 'complete'
                            : 'forward';
        setStatusAction(actionKind);
        try {
            const updates: Partial<NewLocationSurvey> = { status: nextStatus, ...(options?.extraUpdates || {}) };
            await updateNewLocationSurvey(editedSurvey.id, editedSurvey.bankId ?? null, updates);
            const updatedSurvey = { ...editedSurvey, status: nextStatus } as NewLocationSurvey;
            Object.assign(updatedSurvey, options?.extraUpdates || {});
            setEditedSurvey(updatedSurvey);

            if (nextStatus === 'Approved') {
                const { html, subject } = buildLocationSurveyEmail({ survey: updatedSurvey, createdByName: creatorDisplayName });
                const recipients = (options?.recipients || []).filter((email): email is string => Boolean(email));
                const shouldSendEmails = !options?.recipients;

                if (shouldSendEmails) {
                    const defaults = recipientDetails
                        .map((recipient) => recipient.email)
                        .filter((email): email is string => Boolean(email))
                        .map(normalizeEmail);

                    if (defaults.length > 0) {
                        await sendEmailsWithErrorHandling({
                            to: defaults,
                            subject,
                            body: html,
                            isHtml: true,
                            surveyId: editedSurvey.surveyId,
                            surveyType: 'location'
                        });
                        if (onSuccess) {
                            onSuccess(`Survey ${nextStatus.toLowerCase()} successfully. Email sent to: ${defaults.join(', ')}`, updatedSurvey);
                        }
                    } else if (onSuccess) {
                        onSuccess(`Survey ${nextStatus.toLowerCase()} successfully. No email recipients provided.`, updatedSurvey);
                    }
                } else if (onSuccess) {
                    const messageRecipients = recipients.length > 0 ? recipients.join(', ') : 'No email recipients provided';
                    onSuccess(`Survey ${nextStatus.toLowerCase()} successfully. Email sent to: ${messageRecipients}`, updatedSurvey);
                }
            } else if (nextStatus === 'Completed') {
                if (onSuccess) {
                    onSuccess('Survey marked as completed.', updatedSurvey);
                }
            } else if (onSuccess) {
                onSuccess(`Survey ${nextStatus.toLowerCase()} successfully`, updatedSurvey);
            }

            onClose();
        } catch (error) {
            console.error(`Error updating survey status to ${nextStatus}:`, error);
            alert(`Failed to ${nextStatus.toLowerCase()} the survey. Please try again.`);
        } finally {
            setStatusAction(null);
        }
    };

    const handleApproveInitiation = () => {
        if (!editedSurvey) return;
        const defaults = recipientDetails
            .map((recipient) => recipient.email)
            .filter((email): email is string => Boolean(email));
        const uniqueDefaults = Array.from(new Set(defaults.map(normalizeEmail)));
        setEmailRecipients(uniqueDefaults);
        setEmailInputValue('');
        setEmailError(null);
        const { html, subject } = buildLocationSurveyEmail({ survey: editedSurvey, createdByName: creatorDisplayName });
        setEmailPreviewHtml(html);
        setEmailPreviewSubject(subject);
        setShowApproveEmailModal(true);
    };

    const handleForwardInitiation = () => {
        if (!editedSurvey) return;
        setRegionalRemarksInput(editedSurvey.regionalRemarks || '');
        setShowForwardModal(true);
    };

    const handleAddEmail = (rawEmail: string) => {
        const email = normalizeEmail(rawEmail);
        if (!email) return;
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        if (emailRecipients.includes(email)) {
            setEmailError('Recipient already added.');
            return;
        }
        setEmailRecipients((prev) => [...prev, email]);
        setEmailInputValue('');
        setEmailError(null);
    };

    const handleEmailInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddEmail(emailInputValue);
        }
    };

    const handleRemoveEmail = (email: string) => {
        setEmailRecipients((prev) => prev.filter((recipient) => recipient !== email));
    };

    const handleSendApprovalEmail = async () => {
        if (!editedSurvey) return;
        if (emailRecipients.length === 0) {
            setEmailError('Add at least one recipient.');
            return;
        }

        setIsSendingEmail(true);
        setEmailError(null);
        try {
            const extraUpdates = {
                remarks: regionalRemarksInput.trim()
            };

            const surveyWithUpdates = {
                ...editedSurvey,
                status: 'Approved',
                ...extraUpdates
            } as NewLocationSurvey;

            const { html, subject } = buildLocationSurveyEmail({ survey: surveyWithUpdates, createdByName: creatorDisplayName });
            setEmailPreviewHtml(html);
            setEmailPreviewSubject(subject);

            const results = await sendEmailsWithErrorHandling({
                to: emailRecipients,
                subject,
                body: html,
                isHtml: true,
                surveyId: editedSurvey.surveyId,
                surveyType: 'location'
            });

            if (results.failed.length > 0) {
                const failureList = results.failed.map((failure) => `${failure.email}: ${failure.error || 'Unknown error'}`).join(', ');
                setEmailError(`Failed to send to: ${failureList}`);
                setIsSendingEmail(false);
                return;
            }

            await handleStatusUpdate('Approved', { recipients: emailRecipients, extraUpdates });
            setShowApproveEmailModal(false);
        } catch (error) {
            console.error('Error sending approval email:', error);
            setEmailError('Failed to send approval email. Please try again.');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const canShowEditButton = !isEditing && (
        isBankUser || (
            (isSubmittedStatus || isAssignedStatus || status === 'rejected') && isPsoRole && !!uid && (editedSurvey?.createdBy === uid || editedSurvey?.assignedTo === uid)
        )
    );
    const canShowCancelStatus = !isEditing && isPsoRole && !!uid && (
        (isSubmittedStatus && (editedSurvey?.createdBy === uid || editedSurvey?.assignedTo === uid)) ||
        (isApprovedStatus && editedSurvey?.assignedTo === uid)
    );
    const showApproveRejectIncharge = false;
    const showApproveRejectRegional = !isEditing && (status === 'submitted to regional' || (status === 'submitted' && isRegional)) && isRegional;
    const canMarkComplete = !isEditing && isApprovedStatus && isPsoRole && !!uid && editedSurvey?.assignedTo === uid;

    const headerActionButtons: React.ReactNode[] = [];

    if (!isEditing && onDownloadPDF && editedSurvey) {
        headerActionButtons.push(
            <Button
                key="download-pdf"
                variant="secondary"
                onClick={() => onDownloadPDF(editedSurvey)}
                className="flex items-center justify-center !px-3 !py-2 rounded-lg border border-blue-300 text-blue-655 hover:bg-blue-50 dark:border-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-950/20"
                title="Download PDF"
            >
                <Icon icon={FiDownload} className="mr-1.5 h-4 w-4" />
                <span>Download PDF</span>
            </Button>
        );
    }

    if (canShowEditButton) {
        headerActionButtons.push(
            <Button
                key="edit"
                variant="secondary"
                onClick={() => setIsEditing(true)}
                className="flex items-center justify-center !px-2 !py-2 rounded-full border border-gray-400 text-gray-800 hover:bg-gray-100"
                title="Edit Survey"
            >
                <Icon icon={FiEdit2} className="h-4 w-4" />
            </Button>
        );
    }

    const headerActions = headerActionButtons.length > 0 ? (
        <div className="flex items-center gap-2">
            {headerActionButtons}
        </div>
    ) : undefined;

    const directionFields = [
        { key: 'front', label: 'Front' },
        { key: 'back', label: 'Back' },
        { key: 'left', label: 'Left' },
        { key: 'right', label: 'Right' }
    ];

    const questionFields = [
        { key: 'fuelStationAvailable', label: 'Fuel Station Available Near Location?' },
        { key: 'spaceForParking', label: 'Space Available for Parking?' },
        { key: 'spaceForVantagePoint', label: 'Space Available for Vantage Point?' },
        { key: 'spaceForEmergencyExit', label: 'Space Available for Emergency Exit?' },
        { key: 'electricityIssue', label: 'Electricity Issue?' },
        { key: 'ptclLineAvailable', label: 'PTCL Line Available?' }
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Location Survey Details"
            size="lg"
            headerActions={headerActions}
        >
            <div className="p-6">
                <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-gray-200 pb-4 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">Status:</span>
                        <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                isAssignedStatus
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300'
                                    : status === 'completed'
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                        : status === 'submitted to regional'
                                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
                                            : status === 'forwarded'
                                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300'
                                                : status === 'approved'
                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                                                    : status === 'rejected'
                                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                                                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                            }`}
                        >
                            {statusLabel}
                        </span>
                    </div>

                    {!(status === 'assigned' || status === 'submitted' || status === 'submitted to regional') && (
                        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                            <span className="font-semibold text-gray-800 dark:text-gray-200">Incharge Read Status:</span>
                            {isIncharge && onToggleReadStatus ? (
                                <select
                                    value={editedSurvey.inchargeRead ? 'read' : 'unread'}
                                    onChange={async (e) => {
                                        const isRead = e.target.value === 'read';
                                        await onToggleReadStatus(editedSurvey, isRead);
                                        setEditedSurvey(prev => prev ? { ...prev, inchargeRead: isRead } : null);
                                    }}
                                    className={`rounded-full px-2.5 py-1 text-xs font-semibold border border-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 ${
                                        editedSurvey.inchargeRead
                                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 focus:ring-emerald-500' 
                                        : 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800 focus:ring-rose-500 font-bold'
                                    }`}
                                >
                                    <option value="unread" className="bg-white text-rose-850 dark:bg-gray-800 dark:text-rose-350">Unread</option>
                                    <option value="read" className="bg-white text-emerald-850 dark:bg-gray-800 dark:text-emerald-355">Read</option>
                                </select>
                            ) : (
                                <span
                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                        editedSurvey.inchargeRead
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                                    }`}
                                >
                                    {editedSurvey.inchargeRead ? 'Read' : 'Unread'}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">Assigned PSO:</span>
                        <span>{editedSurvey.assignedToName || '—'}</span>
                    </div>
                </div>

                {(editedSurvey.regionalRemarks || editedSurvey.rejectionReason) && (
                    <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50/50 p-5 dark:border-slate-800 dark:bg-slate-900/50">
                        <h4 className="text-sm font-semibold tracking-wide text-slate-800 dark:text-slate-200 uppercase">Regional Head Review</h4>
                        <div className="mt-3 space-y-3">
                            {editedSurvey.regionalRemarks && (
                                <div className="text-sm">
                                    <span className="font-medium text-slate-500 dark:text-slate-400">Regional Remarks:</span>
                                    <p className="mt-1 font-normal text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{editedSurvey.regionalRemarks}</p>
                                </div>
                            )}
                            {editedSurvey.rejectionReason && (
                                <div className="text-sm">
                                    <span className="font-medium text-red-600 dark:text-red-400">Rejection Reason:</span>
                                    <p className="mt-1 font-normal text-red-700 dark:text-red-300 whitespace-pre-wrap">{editedSurvey.rejectionReason}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    <div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Survey Information</h4>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Survey No.</label>
                                <div className="rounded-xl border border-gray-300 bg-gray-50 py-3 px-4 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600">
                                    {editedSurvey.surveyId || 'N/A'}
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Survey Request Date</label>
                                {isEditing ? (
                                    <TextField
                                        type="date"
                                        value={editedSurvey.surveyRequestDate || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('surveyRequestDate', e.target.value)}
                                    />
                                ) : (
                                    <div className="rounded-xl border border-gray-300 bg-gray-50 py-3 px-4 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600">
                                        {editedSurvey.surveyRequestDate || 'N/A'}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Survey Date</label>
                                {isEditing ? (
                                    <TextField
                                        type="date"
                                        value={editedSurvey.surveyDate || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('surveyDate', e.target.value)}
                                    />
                                ) : (
                                    <div className="rounded-xl border border-gray-300 bg-gray-50 py-3 px-4 text-sm dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600">
                                        {editedSurvey.surveyDate || 'N/A'}
                                    </div>
                                )}
                            </div>
                         </div>
                    </div>

                    <div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Directions</h4>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            {directionFields.map(({ key, label }) => (
                                <div key={key}>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">{label}</label>
                                    {isEditing ? (
                                        <TextField
                                            value={(editedSurvey.directions?.[key] as string) || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDirectionChange(key, e.target.value)}
                                            placeholder={`Enter ${label.toLowerCase()} direction details`}
                                        />
                                    ) : (
                                        <div className="rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm dark:bg-black/30 dark:text-gray-100 dark:border-gray-600">
                                            {editedSurvey.directions?.[key] || 'N/A'}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Location Details</h4>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Location Address</label>
                                {isEditing ? (
                                    <textarea
                                        rows={3}
                                        value={editedSurvey.locationAddress || ''}
                                        onChange={(e) => handleInputChange('locationAddress', e.target.value)}
                                        placeholder="Enter branch / site address"
                                        className="w-full rounded-xl border border-gray-300 py-3 px-4 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    />
                                ) : (
                                    <div className="rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm dark:bg-black/30 dark:text-gray-100 dark:border-gray-600">
                                        {editedSurvey.locationAddress || 'N/A'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Assessment Questions</h4>
                        <div className="mt-4 space-y-4">
                            {questionFields.map(({ key, label }) => (
                                <div key={key}>
                                    <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">{label}</label>
                                    {isEditing ? (
                                        <div className="flex gap-4 text-sm">
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name={key}
                                                    value="Yes"
                                                    checked={editedSurvey.questions?.[key] === 'Yes'}
                                                    onChange={(e) => handleQuestionChange(key, e.target.value)}
                                                    className="h-4 w-4"
                                                />
                                                Yes
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name={key}
                                                    value="No"
                                                    checked={editedSurvey.questions?.[key] === 'No'}
                                                    onChange={(e) => handleQuestionChange(key, e.target.value)}
                                                    className="h-4 w-4"
                                                />
                                                No
                                            </label>
                                        </div>
                                    ) : (
                                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${editedSurvey.questions?.[key] === 'Yes'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                            }`}>
                                            {editedSurvey.questions?.[key] || 'Not Provided'}
                                        </span>
                                    )}
                                </div>
                            ))}
                            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-black/30">
                                <div className="mb-2 flex items-center justify-between">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Any Other Assessment?</label>
                                    {!isEditing && (
                                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${otherAssessment === 'Yes'
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800/40 dark:text-gray-300'}`}>
                                            {otherAssessment === 'Yes' ? 'Yes' : 'No'}
                                        </span>
                                    )}
                                </div>
                                {isEditing ? (
                                    <div className="space-y-3 text-sm">
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="otherAssessment"
                                                    value="Yes"
                                                    checked={otherAssessment === 'Yes'}
                                                    onChange={(e) => handleQuestionChange('otherAssessment', e.target.value)}
                                                    className="h-4 w-4"
                                                />
                                                Yes
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="otherAssessment"
                                                    value="No"
                                                    checked={otherAssessment === 'No'}
                                                    onChange={(e) => handleQuestionChange('otherAssessment', e.target.value)}
                                                    className="h-4 w-4"
                                                />
                                                No
                                            </label>
                                        </div>
                                        {otherAssessment === 'Yes' && (
                                            <>
                                                <textarea
                                                    rows={3}
                                                    value={editedSurvey.hazardousArea || ''}
                                                    onChange={(e) => handleOtherAssessmentDetailsChange(e.target.value)}
                                                    placeholder="Describe the assessment"
                                                    className="w-full rounded-xl border border-gray-300 py-3 px-4 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                                />
                                                <div className="flex gap-4">
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name="otherAssessmentHazardous"
                                                            value="Yes"
                                                            checked={otherAssessmentHazardous === 'Yes'}
                                                            onChange={(e) => handleQuestionChange('otherAssessmentHazardous', e.target.value)}
                                                            className="h-4 w-4"
                                                        />
                                                        Hazardous
                                                    </label>
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name="otherAssessmentHazardous"
                                                            value="No"
                                                            checked={otherAssessmentHazardous !== 'Yes'}
                                                            onChange={(e) => handleQuestionChange('otherAssessmentHazardous', e.target.value)}
                                                            className="h-4 w-4"
                                                        />
                                                        Not Hazardous
                                                    </label>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-3 text-sm">
                                        {otherAssessment === 'Yes' ? (
                                            <>
                                                <div className="rounded-xl border border-gray-200 bg-white py-2 px-3 dark:border-gray-700 dark:bg-black/20">
                                                    {editedSurvey.hazardousArea || '—'}
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-gray-700 dark:text-gray-200">Hazardous:</span>
                                                    <span className={`ml-2 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${otherAssessmentHazardous === 'Yes'
                                                        ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                                                        : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'}`}>
                                                        {otherAssessmentHazardous === 'Yes' ? 'Yes' : 'No'}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-gray-500 dark:text-gray-400">No additional assessment reported.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Space Details</h4>
                        <div className="mt-4">
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Space Consists Of</label>
                            {isEditing ? (
                                <SearchableSelect
                                    value={editedSurvey.spaceConsistsOf || ''}
                                    onChange={(value: string) => handleInputChange('spaceConsistsOf', value)}
                                    options={[
                                        { id: 'space-select', label: 'Select option', value: '' },
                                        { id: 'space-ground-only', label: 'Ground Only', value: 'Ground Only' },
                                        { id: 'space-ground-basement', label: 'Ground + Basement', value: 'Ground + Basement' },
                                        { id: 'space-ground-basement-first', label: 'Ground + Basement + First Floor', value: 'Ground + Basement + First Floor' },
                                        { id: 'space-ground-first', label: 'Ground + First Floor', value: 'Ground + First Floor' },
                                        { id: 'space-ground-first-second', label: 'Ground + First Floor + Second Floor', value: 'Ground + First Floor + Second Floor' },
                                        { id: 'space-multi-story', label: 'Multi-story Building', value: 'Multi-story Building' },
                                        { id: 'space-commercial', label: 'Commercial Complex', value: 'Commercial Complex' },
                                        { id: 'space-industrial', label: 'Industrial Area', value: 'Industrial Area' },
                                        { id: 'space-residential', label: 'Residential Complex', value: 'Residential Complex' },
                                        { id: 'space-other', label: 'Other', value: 'Other' }
                                    ]}
                                />
                            ) : (
                                <div className="rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm dark:bg-black/30 dark:text-gray-100 dark:border-gray-600">
                                    {editedSurvey.spaceConsistsOf || 'N/A'}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Remarks</h4>
                        <div className="mt-4">
                            {isEditing ? (
                                <textarea
                                    value={editedSurvey.remarks || ''}
                                    onChange={(e) => handleInputChange('remarks', e.target.value)}
                                    rows={4}
                                    className="w-full rounded-xl border border-gray-300 py-3 px-4 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                    placeholder="Enter any additional remarks"
                                />
                            ) : (
                                <div className="rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm dark:bg-black/30 dark:text-gray-100 dark:border-gray-600">
                                    <span className="whitespace-pre-wrap">{editedSurvey.remarks || 'N/A'}</span>
                                </div>
                            )}
                        </div>
                    </div>



                </div>

                {/* Footer Actions */}
                <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                    {isEditing ? (
                        <>
                            <Button
                                variant="secondary"
                                onClick={handleCancelEdit}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={loading}
                            >
                                <Icon icon={FiSave} className="mr-2 h-4 w-4" />
                                {loading ? 'Saving...' : (isAssignedStatus ? 'Submit Survey' : 'Save Changes')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="secondary"
                                onClick={onClose}
                                disabled={statusAction !== null}
                                className="border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Close
                            </Button>

                            {canShowCancelStatus && (
                                <Button
                                    variant="secondary"
                                    className="border border-red-300 text-red-650 hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-950/20"
                                    onClick={() => {
                                        setCancelReason('');
                                        setCancelError(null);
                                        setShowCancelModal(true);
                                    }}
                                    disabled={statusAction === 'cancel'}
                                >
                                    <Icon icon={FiXCircle} className="mr-2 h-4 w-4" />
                                    Cancel Request
                                </Button>
                            )}

                            {canMarkComplete && (
                                <Button
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => setShowCompleteModal(true)}
                                    disabled={statusAction === 'approve'}
                                >
                                    <Icon icon={FiCheck} className="mr-2 h-4 w-4" />
                                    Mark as Completed
                                </Button>
                            )}

                            {showApproveRejectIncharge && (
                                <>
                                    <Button
                                        variant="secondary"
                                        className="border border-red-300 text-red-650 hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-950/20"
                                        onClick={() => {
                                            setRejectionReasonInput('');
                                            setRejectError(null);
                                            setShowRejectModal(true);
                                        }}
                                        disabled={statusAction === 'reject'}
                                    >
                                        <Icon icon={FiXCircle} className="mr-2 h-4 w-4" />
                                        Reject Request
                                    </Button>
                                    <Button
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={handleApproveInitiation}
                                        disabled={statusAction === 'approve'}
                                    >
                                        <Icon icon={FiCheckCircle} className="mr-2 h-4 w-4" />
                                        Approve Request
                                    </Button>
                                </>
                            )}

                            {showApproveRejectRegional && (
                                <>
                                    <Button
                                        variant="secondary"
                                        className="border border-red-300 text-red-655 hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-950/20"
                                        onClick={() => {
                                            setRejectionReasonInput('');
                                            setRejectError(null);
                                            setShowRejectModal(true);
                                        }}
                                        disabled={statusAction === 'reject'}
                                    >
                                        <Icon icon={FiXCircle} className="mr-2 h-4 w-4" />
                                        Reject Request
                                    </Button>
                                    <Button
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={handleForwardInitiation}
                                        disabled={statusAction === 'forward'}
                                    >
                                        <Icon icon={FiCheckCircle} className="mr-2 h-4 w-4" />
                                        Forward to Incharge
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showApproveEmailModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-2xl rounded-xl border border-gray-300 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {isRegional ? 'Forward to Incharge' : 'Send Approval Email'}
                            </h3>
                            <button
                                onClick={() => {
                                    if (!isSendingEmail) {
                                        setShowApproveEmailModal(false);
                                        setEmailError(null);
                                    }
                                }}
                                className="rounded-lg p-1 text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {isRegional ? 'Regional Remarks' : 'Approval Remarks'} (Optional)
                                </label>
                                <textarea
                                    value={regionalRemarksInput}
                                    onChange={(e) => setRegionalRemarksInput(e.target.value)}
                                    rows={3}
                                    placeholder={isRegional ? 'Enter remarks for the Incharge...' : 'Enter approval remarks...'}
                                    className="w-full rounded-xl border border-gray-300 p-3 text-sm text-gray-800 outline-none focus:border-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                    disabled={isSendingEmail}
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">Recipients</label>
                                <div className="rounded-xl border border-gray-300 p-3 bg-white dark:border-gray-700 dark:bg-gray-800">
                                    <div className="flex flex-wrap gap-2">
                                        {emailRecipients.map((email) => (
                                            <span key={email} className="inline-flex items-center gap-2 rounded-full border border-gray-400 px-3 py-1 text-xs font-semibold text-gray-800 dark:border-gray-500 dark:text-gray-100">
                                                {email}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveEmail(email)}
                                                    className="text-gray-600 hover:text-black dark:text-gray-300 dark:hover:text-white"
                                                    aria-label={`Remove ${email}`}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                        <input
                                            type="email"
                                            value={emailInputValue}
                                            onChange={(event) => setEmailInputValue(event.target.value)}
                                            onKeyDown={handleEmailInputKeyDown}
                                            placeholder="Type email and press Enter"
                                            className="flex-1 min-w-[200px] border-none bg-transparent text-sm text-gray-700 outline-none dark:text-gray-100"
                                            disabled={isSendingEmail}
                                        />
                                    </div>
                                </div>
                                {emailError && <p className="mt-2 text-xs text-red-500">{emailError}</p>}
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">Email Preview</label>
                                {emailPreviewSubject && (
                                    <div className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Subject: {emailPreviewSubject}</div>
                                )}
                                <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-300 bg-white p-4 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                                    <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button variant="secondary" onClick={() => { if (!isSendingEmail) { setShowApproveEmailModal(false); setEmailError(null); } }} disabled={isSendingEmail} className="border border-gray-400 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                                Cancel
                            </Button>
                            <Button onClick={handleSendApprovalEmail} disabled={isSendingEmail} className="border border-gray-800 bg-gray-900 text-white hover:bg-black dark:border-gray-500 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-white">
                                {isSendingEmail ? 'Sending…' : (isRegional ? 'Forward & Send Email' : 'Send Email')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-lg rounded-xl border border-gray-300 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Cancel Survey Request</h3>
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Please provide a reason for cancelling this survey.</p>
                        <textarea
                            value={cancelReason}
                            onChange={(event) => setCancelReason(event.target.value)}
                            rows={4}
                            className="mt-4 w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-800 outline-none focus:border-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Enter cancellation reason"
                            disabled={statusAction === 'cancel'}
                        />
                        {cancelError && <p className="mt-2 text-xs text-red-500">{cancelError}</p>}
                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => { if (statusAction !== 'cancel') { setShowCancelModal(false); setCancelError(null); } }}
                                disabled={statusAction === 'cancel'}
                                className="border border-gray-400 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Close
                            </Button>
                            <Button
                                onClick={async () => {
                                    if (!cancelReason.trim()) {
                                        setCancelError('Cancellation reason is required.');
                                        return;
                                    }
                                    setCancelError(null);
                                    await handleStatusUpdate('Cancelled', {
                                        extraUpdates: {
                                            cancellationReason: cancelReason.trim()
                                        }
                                    });
                                    setShowCancelModal(false);
                                }}
                                disabled={statusAction === 'cancel'}
                                className="border border-gray-800 bg-gray-900 text-white hover:bg-black dark:border-gray-500 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-white"
                            >
                                Confirm Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showRejectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-lg rounded-xl border border-gray-300 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reject Survey Request</h3>
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Please provide a reason for rejecting this survey.</p>
                        <textarea
                            value={rejectionReasonInput}
                            onChange={(event) => setRejectionReasonInput(event.target.value)}
                            rows={4}
                            className="mt-4 w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-800 outline-none focus:border-gray-550 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Enter rejection reason (optional)"
                            disabled={statusAction === 'reject'}
                        />
                        <div className="mt-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {isRegional ? 'Regional Remarks' : 'Rejection Remarks'} (Optional)
                            </label>
                            <textarea
                                value={regionalRemarksInput}
                                onChange={(e) => setRegionalRemarksInput(e.target.value)}
                                rows={3}
                                placeholder="Enter additional remarks..."
                                className="w-full rounded-xl border border-gray-300 p-3 text-sm text-gray-800 outline-none focus:border-gray-550 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                disabled={statusAction === 'reject'}
                            />
                        </div>
                        {rejectError && <p className="mt-2 text-xs text-red-500">{rejectError}</p>}
                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => { if (statusAction !== 'reject') { setShowRejectModal(false); setRejectError(null); } }}
                                disabled={statusAction === 'reject'}
                                className="border border-gray-400 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Close
                            </Button>
                            <Button
                                onClick={async () => {
                                    setRejectError(null);
                                    await handleStatusUpdate('Rejected', {
                                        extraUpdates: {
                                            rejectionReason: rejectionReasonInput.trim() || undefined,
                                            ...(isRegional 
                                                ? { regionalRemarks: regionalRemarksInput.trim() || undefined } 
                                                : { remarks: regionalRemarksInput.trim() || undefined }
                                            )
                                        }
                                    });
                                    setShowRejectModal(false);
                                }}
                                disabled={statusAction === 'reject'}
                                className="border border-gray-800 bg-gray-900 text-white hover:bg-black dark:border-gray-500 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-white"
                            >
                                Confirm Reject
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showCompleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-md rounded-xl border border-gray-300 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Mark Survey as Completed</h3>
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Are you sure you want to mark this survey as completed?</p>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => setShowCompleteModal(false)}
                                disabled={statusAction === 'complete'}
                                className="border border-gray-400 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                No
                            </Button>
                            <Button
                                onClick={async () => {
                                    await handleStatusUpdate('Completed', {
                                        extraUpdates: {
                                            completedBy: uid ?? undefined,
                                            completedAt: new Date()
                                        }
                                    });
                                    setShowCompleteModal(false);
                                }}
                                disabled={statusAction === 'complete'}
                                className="border border-gray-800 bg-gray-900 text-white hover:bg-black dark:border-gray-500 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-white"
                            >
                                Yes, Completed
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showForwardModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-lg rounded-xl border border-gray-300 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Forward Survey to Incharge</h3>
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">You can optionally add regional remarks before forwarding this survey.</p>
                        <div className="mt-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-800 dark:text-gray-200">Regional Remarks (Optional)</label>
                            <textarea
                                value={regionalRemarksInput}
                                onChange={(e) => setRegionalRemarksInput(e.target.value)}
                                rows={4}
                                placeholder="Enter remarks for the Incharge..."
                                className="w-full rounded-xl border border-gray-300 p-3 text-sm text-gray-800 outline-none focus:border-gray-550 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                                disabled={statusAction === 'forward'}
                            />
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => { if (statusAction !== 'forward') { setShowForwardModal(false); } }}
                                disabled={statusAction === 'forward'}
                                className="border border-gray-400 text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Close
                            </Button>
                            <Button
                                onClick={async () => {
                                    const extraUpdates = {
                                        regionalRemarks: regionalRemarksInput.trim(),
                                        inchargeRead: false,
                                        forwardedBy: uid ?? undefined,
                                        forwardedByName: currentUser?.displayName || designation || 'Regional User',
                                        forwardedAt: new Date()
                                    };
                                    await handleStatusUpdate('Forwarded', { extraUpdates });
                                    setShowForwardModal(false);
                                }}
                                disabled={statusAction === 'forward'}
                                className="bg-blue-600 text-white hover:bg-blue-700"
                            >
                                {statusAction === 'forward' ? 'Forwarding...' : 'Confirm Forward'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}
